import React, { useState, useEffect } from 'react';
import ReactFlow, { Background, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import axios from 'axios';
import { API_BASE_URL } from './config';
import JsonEditor from './components/JsonEditor';
import CopilotModal from './components/CopilotModal';
import { formatLocalDateTime, localDateTimeInputToUtcIso, toEpochMs } from './timezone';


const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--bg-modal-overlay)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
};

const modalContentStyle = {
    backgroundColor: 'var(--bg-modal)',
    padding: '20px',
    borderRadius: '8px',
    width: '80%',
    maxWidth: '800px',
    maxHeight: '80%',
    overflowY: 'auto',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-color)',
    boxShadow: '0 4px 6px var(--shadow-strong)'
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '20px'
};

const thStyle = {
    textAlign: 'left',
    padding: '10px',
    borderBottom: '1px solid var(--border-color)',
    color: 'var(--text-secondary)'
};

const tdStyle = {
    padding: '10px',
    borderBottom: '1px solid var(--border-color)'
};

const statusStyle = (status) => ({
    color: status === 'completed' ? 'var(--success-color)' : status === 'failed' ? 'var(--error-color)' : 'var(--warning-color)',
    fontWeight: 'bold',
    textTransform: 'capitalize'
});

// ── Step-Through Debugger ────────────────────────────────────────────────────

function StepThroughDebugger({ run, nodes, onClose, getAuthHeaders }) {
    const executionOrder = run?.outputs?.env?._execution_order || {};
    const storedNodes = run?.outputs?.nodes || {};
    const errorMap = run?.outputs?.env?._errors || {};

    // Sort node IDs by their execution order value
    const orderedNodeIds = Object.entries(executionOrder)
        .sort((a, b) => a[1] - b[1])
        .map(([id]) => id)
        .filter(id => storedNodes[id] !== undefined || errorMap[id]);

    const [stepIndex, setStepIndex] = useState(0);
    const [replayResults, setReplayResults] = useState({});
    const [replayLoading, setReplayLoading] = useState({});

    const totalSteps = orderedNodeIds.length;

    if (totalSteps === 0) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                No execution steps found in this run.
                <br />
                <button
                    onClick={onClose}
                    style={{ marginTop: '16px', padding: '6px 14px', background: 'var(--accent-color)', color: 'var(--text-inverse)', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                    Close
                </button>
            </div>
        );
    }

    const currentNodeId = orderedNodeIds[stepIndex];
    const currentOrder = executionOrder[currentNodeId];
    const currentNodeDef = nodes?.find(n => n.id === currentNodeId);
    const currentLabel = currentNodeDef?.data?.label || currentNodeId;
    const currentOutput = storedNodes[currentNodeId];
    const currentError = errorMap[currentNodeId];
    const hasError = Boolean(currentError);

    const replayResult = replayResults[currentNodeId];
    const isReplaying = replayLoading[currentNodeId];

    const handleReplay = async () => {
        setReplayLoading(prev => ({ ...prev, [currentNodeId]: true }));
        try {
            const res = await axios.post(
                `${API_BASE_URL}/runs/${run.id}/replay-node`,
                { node_id: currentNodeId },
                { headers: getAuthHeaders() }
            );
            setReplayResults(prev => ({ ...prev, [currentNodeId]: res.data }));
        } catch (err) {
            setReplayResults(prev => ({
                ...prev,
                [currentNodeId]: { success: false, error: err.message || 'Request failed' }
            }));
        } finally {
            setReplayLoading(prev => ({ ...prev, [currentNodeId]: false }));
        }
    };

    return (
        <div>
            {/* Progress bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <button
                    onClick={onClose}
                    style={{ padding: '5px 10px', background: 'transparent', color: 'var(--accent-color)', border: '1px solid var(--accent-color)', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                    ✕ Close Debugger
                </button>
                <div style={{ flex: 1, height: '6px', background: 'var(--bg-input)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${((stepIndex + 1) / totalSteps) * 100}%`, background: 'var(--accent-color)', borderRadius: '3px', transition: 'width 0.2s ease' }} />
                </div>
                <span style={{ color: 'var(--text-tertiary)', fontSize: '13px', whiteSpace: 'nowrap' }}>
                    Step {stepIndex + 1} / {totalSteps}
                </span>
            </div>

            {/* Step card */}
            <div style={{
                background: 'var(--bg-secondary)',
                border: `1px solid ${hasError ? 'var(--error-color)' : 'var(--border-color)'}`,
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                            Execution order #{currentOrder}
                        </div>
                        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{currentLabel}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{currentNodeId}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {hasError
                            ? <span style={{ color: 'var(--error-color)', fontWeight: 'bold', fontSize: '13px' }}>✗ Failed</span>
                            : <span style={{ color: 'var(--success-color)', fontWeight: 'bold', fontSize: '13px' }}>✓ Succeeded</span>
                        }
                        <button
                            onClick={handleReplay}
                            disabled={isReplaying}
                            title="Re-execute this node with its original inputs"
                            style={{
                                padding: '5px 12px',
                                background: isReplaying ? 'var(--button-bg)' : 'var(--accent-color)',
                                color: isReplaying ? 'var(--text-tertiary)' : 'var(--text-inverse)',
                                border: 'none', borderRadius: '4px', cursor: isReplaying ? 'not-allowed' : 'pointer',
                                fontSize: '12px'
                            }}
                        >
                            {isReplaying ? '⟳ Replaying…' : '↩ Replay Node'}
                        </button>
                    </div>
                </div>

                {/* Original output / error */}
                {hasError ? (
                    <div style={{ color: 'var(--error-color)', padding: '8px', background: 'var(--bg-input)', borderRadius: '4px', fontSize: '13px', marginBottom: replayResult ? '12px' : 0 }}>
                        <strong>Error:</strong> {currentError}
                    </div>
                ) : (
                    <div>
                        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>Original output:</div>
                        <JsonEditor
                            value={JSON.stringify(currentOutput?.data, null, 2)}
                            onChange={() => {}}
                            readOnly={true}
                            style={{ minHeight: '60px', border: 'none', background: 'var(--bg-input)', padding: '8px', borderRadius: '4px' }}
                        />
                    </div>
                )}

                {/* Replay result */}
                {replayResult && (
                    <div style={{ marginTop: '12px', borderTop: '1px solid var(--border-color)', paddingTop: '12px' }}>
                        <div style={{ fontSize: '12px', marginBottom: '4px', color: replayResult.success ? 'var(--success-color)' : 'var(--error-color)', fontWeight: 'bold' }}>
                            {replayResult.success ? '✓ Replay result' : '✗ Replay failed'} {replayResult.duration_ms !== undefined ? `(${replayResult.duration_ms}ms)` : ''}
                        </div>
                        {replayResult.success ? (
                            <JsonEditor
                                value={JSON.stringify(replayResult.output, null, 2)}
                                onChange={() => {}}
                                readOnly={true}
                                style={{ minHeight: '60px', border: 'none', background: 'var(--bg-input)', padding: '8px', borderRadius: '4px' }}
                            />
                        ) : (
                            <div style={{ color: 'var(--error-color)', padding: '8px', background: 'var(--bg-input)', borderRadius: '4px', fontSize: '13px' }}>
                                {replayResult.error}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Navigation */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                    onClick={() => setStepIndex(i => Math.max(0, i - 1))}
                    disabled={stepIndex === 0}
                    style={{
                        padding: '7px 18px',
                        background: stepIndex === 0 ? 'var(--button-bg)' : 'var(--accent-color)',
                        color: stepIndex === 0 ? 'var(--text-tertiary)' : 'var(--text-inverse)',
                        border: 'none', borderRadius: '4px',
                        cursor: stepIndex === 0 ? 'not-allowed' : 'pointer'
                    }}
                >
                    ← Previous
                </button>
                <button
                    onClick={() => setStepIndex(i => Math.min(totalSteps - 1, i + 1))}
                    disabled={stepIndex === totalSteps - 1}
                    style={{
                        padding: '7px 18px',
                        background: stepIndex === totalSteps - 1 ? 'var(--button-bg)' : 'var(--accent-color)',
                        color: stepIndex === totalSteps - 1 ? 'var(--text-tertiary)' : 'var(--text-inverse)',
                        border: 'none', borderRadius: '4px',
                        cursor: stepIndex === totalSteps - 1 ? 'not-allowed' : 'pointer'
                    }}
                >
                    Next →
                </button>
            </div>
        </div>
    );
}

// ── Main Component ───────────────────────────────────────────────────────────

const PRESET_STORAGE_KEY = (workflowId) => `xavvy_run_filter_presets_${workflowId}`;

const EMPTY_FILTERS = {
    status: '',
    trigger_type: '',
    date_from: '',
    date_to: '',
    min_duration_s: '',
    max_duration_s: '',
    error_contains: '',
};

export default function WorkflowHistory({ workflowId, nodes, edges, onClose, onPinTriggerData, onLoadRunOutputs }) {
    const [runs, setRuns] = useState([]);
    const [selectedRunIds, setSelectedRunIds] = useState([]);
    const [selectedRun, setSelectedRun] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);
    const [total, setTotal] = useState(0);
    const [saveHistory, setSaveHistory] = useState(true);
    const [workflowData, setWorkflowData] = useState(null);
    const [copilotState, setCopilotState] = useState({ isOpen: false, mode: 'fix', runId: null });
    const [stepThroughMode, setStepThroughMode] = useState(false);
    const [nodeReplay, setNodeReplay] = useState({}); // { [nodeId]: { loading, result } }
    const [nodeResume, setNodeResume] = useState({}); // { [nodeId]: { loading, result } }
    const [loadingRunOutputs, setLoadingRunOutputs] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);

    // Filter state
    const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
    const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS });
    const [showFilters, setShowFilters] = useState(false);
    const [presets, setPresets] = useState(() => {
        try { return JSON.parse(localStorage.getItem(PRESET_STORAGE_KEY(workflowId)) || '[]'); }
        catch { return []; }
    });
    const [presetName, setPresetName] = useState('');
    const [exportLoading, setExportLoading] = useState(false);

    // ── Test Suite state ──────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState('history');
    const [testCases, setTestCases] = useState([]);
    const [testCasesLoading, setTestCasesLoading] = useState(false);
    const [testRunLoading, setTestRunLoading] = useState(false);
    const [testResults, setTestResults] = useState(null);   // null = not run yet
    const [editingCaseId, setEditingCaseId] = useState(null);
    const [editingCaseDraft, setEditingCaseDraft] = useState(null);
    const [addingCase, setAddingCase] = useState(false);
    const [newCaseDraft, setNewCaseDraft] = useState({ name: '', trigger_payload: '{}', assertions: '[]' });
    const [testSuiteError, setTestSuiteError] = useState('');

    useEffect(() => {
        fetchWorkflowSettings();
        fetchRuns(1, appliedFilters);
    }, [workflowId]);

    useEffect(() => {
        fetchRuns(currentPage, appliedFilters);
    }, [currentPage]);

    useEffect(() => {
        if (!autoRefresh || activeTab !== 'history' || selectedRun) return;
        const timer = setInterval(() => {
            fetchRuns(currentPage, appliedFilters, { silent: true });
        }, 5000);
        return () => clearInterval(timer);
    }, [autoRefresh, activeTab, selectedRun, currentPage, appliedFilters, workflowId]);

    // Reset debugger when run changes
    useEffect(() => {
        setStepThroughMode(false);
        setNodeReplay({});
        setNodeResume({});
    }, [selectedRun?.id]);

    useEffect(() => {
        if (activeTab === 'tests') fetchTestCases();
    }, [activeTab]);

    const getAuthHeaders = () => {
        const token = localStorage.getItem('token');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    const fetchWorkflowSettings = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/workflows/${workflowId}`, { headers: getAuthHeaders() });
            setWorkflowData(res.data);
            setSaveHistory(res.data.save_history !== undefined ? res.data.save_history : true);
        } catch (error) {
            console.error("Failed to fetch workflow settings", error);
        }
    };

    const toggleSaveHistory = async () => {
        try {
            const newSaveHistory = !saveHistory;
            setSaveHistory(newSaveHistory);
            await axios.put(`${API_BASE_URL}/workflows/${workflowId}`, {
                ...workflowData,
                save_history: newSaveHistory
            }, { headers: getAuthHeaders() });
            setWorkflowData(prev => ({ ...prev, save_history: newSaveHistory }));
        } catch (error) {
            console.error("Failed to toggle save history", error);
            setSaveHistory(!saveHistory);
            alert("Failed to update history settings");
        }
    };

    const fetchRuns = async (page = currentPage, activeFilters = appliedFilters, options = {}) => {
        const { silent = false } = options;
        try {
            if (!silent) setLoading(true);
            const skip = (page - 1) * limit;
            const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
            if (activeFilters.status) params.set('status', activeFilters.status);
            if (activeFilters.trigger_type) params.set('trigger_type', activeFilters.trigger_type);
            if (activeFilters.date_from) params.set('date_from', localDateTimeInputToUtcIso(activeFilters.date_from));
            if (activeFilters.date_to) params.set('date_to', localDateTimeInputToUtcIso(activeFilters.date_to));
            if (activeFilters.min_duration_s) params.set('min_duration_s', activeFilters.min_duration_s);
            if (activeFilters.max_duration_s) params.set('max_duration_s', activeFilters.max_duration_s);
            if (activeFilters.error_contains) params.set('error_contains', activeFilters.error_contains);
            const res = await axios.get(`${API_BASE_URL}/workflows/${workflowId}/runs?${params}`, { headers: getAuthHeaders() });
            if (res.data.runs) {
                setRuns(res.data.runs);
                setTotal(res.data.total);
                setSelectedRunIds(prev => prev.filter(id => res.data.runs.some(run => run.id === id)));
            } else {
                const normalizedRuns = Array.isArray(res.data) ? res.data : [];
                setRuns(normalizedRuns);
                setTotal(normalizedRuns.length);
                setSelectedRunIds(prev => prev.filter(id => normalizedRuns.some(run => run.id === id)));
            }
        } catch (error) {
            console.error("Failed to fetch runs", error);
            alert("Failed to fetch execution history");
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const applyFilters = () => {
        setAppliedFilters({ ...filters });
        setCurrentPage(1);
        fetchRuns(1, filters);
    };

    const clearFilters = () => {
        setFilters({ ...EMPTY_FILTERS });
        setAppliedFilters({ ...EMPTY_FILTERS });
        setCurrentPage(1);
        fetchRuns(1, EMPTY_FILTERS);
    };

    const savePreset = () => {
        if (!presetName.trim()) return;
        const updated = [...presets, { name: presetName.trim(), filters: { ...filters } }];
        setPresets(updated);
        localStorage.setItem(PRESET_STORAGE_KEY(workflowId), JSON.stringify(updated));
        setPresetName('');
    };

    const loadPreset = (preset) => {
        setFilters({ ...preset.filters });
    };

    const deletePreset = (idx) => {
        const updated = presets.filter((_, i) => i !== idx);
        setPresets(updated);
        localStorage.setItem(PRESET_STORAGE_KEY(workflowId), JSON.stringify(updated));
    };

    const exportCsv = async () => {
        setExportLoading(true);
        try {
            const params = new URLSearchParams();
            if (appliedFilters.status) params.set('status', appliedFilters.status);
            if (appliedFilters.trigger_type) params.set('trigger_type', appliedFilters.trigger_type);
            if (appliedFilters.date_from) params.set('date_from', localDateTimeInputToUtcIso(appliedFilters.date_from));
            if (appliedFilters.date_to) params.set('date_to', localDateTimeInputToUtcIso(appliedFilters.date_to));
            if (appliedFilters.min_duration_s) params.set('min_duration_s', appliedFilters.min_duration_s);
            if (appliedFilters.max_duration_s) params.set('max_duration_s', appliedFilters.max_duration_s);
            if (appliedFilters.error_contains) params.set('error_contains', appliedFilters.error_contains);
            const res = await axios.get(
                `${API_BASE_URL}/workflows/${workflowId}/runs/export?${params}`,
                { headers: getAuthHeaders(), responseType: 'blob' }
            );
            const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `runs_${workflowId}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            alert('Export failed');
        } finally {
            setExportLoading(false);
        }
    };

    // ── Test Suite helpers ────────────────────────────────────────────────────
    const fetchTestCases = async () => {
        setTestCasesLoading(true);
        setTestSuiteError('');
        try {
            const res = await axios.get(`${API_BASE_URL}/workflows/${workflowId}/test-suite`, { headers: getAuthHeaders() });
            setTestCases(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setTestSuiteError(err.response?.data?.detail || 'Failed to load test cases');
        } finally {
            setTestCasesLoading(false);
        }
    };

    const handleRunTestSuite = async () => {
        setTestRunLoading(true);
        setTestResults(null);
        setTestSuiteError('');
        try {
            const res = await axios.post(`${API_BASE_URL}/workflows/${workflowId}/test-suite/run`, {}, { headers: getAuthHeaders() });
            setTestResults(res.data);
        } catch (err) {
            setTestSuiteError(err.response?.data?.detail || 'Test run failed');
        } finally {
            setTestRunLoading(false);
        }
    };

    const handleDownloadJUnit = () => {
        const token = localStorage.getItem('token');
        const url = `${API_BASE_URL}/workflows/${workflowId}/test-suite/junit.xml`;
        const a = document.createElement('a');
        a.href = token ? `${url}?token=${encodeURIComponent(token)}` : url;
        a.setAttribute('download', `junit_${workflowId}.xml`);
        // Use fetch with auth header instead
        fetch(url, { headers: getAuthHeaders() })
            .then(r => r.blob())
            .then(blob => {
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `junit_${workflowId}.xml`;
                link.click();
                URL.revokeObjectURL(blobUrl);
            })
            .catch(() => setTestSuiteError('Failed to download JUnit report'));
    };

    const handleCreateTestCase = async () => {
        setTestSuiteError('');
        let payload, assertions;
        try { payload = JSON.parse(newCaseDraft.trigger_payload || '{}'); } catch { setTestSuiteError('trigger_payload is not valid JSON'); return; }
        try { assertions = JSON.parse(newCaseDraft.assertions || '[]'); } catch { setTestSuiteError('assertions is not valid JSON'); return; }
        try {
            await axios.post(`${API_BASE_URL}/workflows/${workflowId}/test-suite`, {
                name: newCaseDraft.name,
                trigger_payload: payload,
                assertions,
            }, { headers: getAuthHeaders() });
            setAddingCase(false);
            setNewCaseDraft({ name: '', trigger_payload: '{}', assertions: '[]' });
            fetchTestCases();
        } catch (err) {
            setTestSuiteError(err.response?.data?.detail || 'Failed to create test case');
        }
    };

    const handleSaveTestCase = async (caseId) => {
        setTestSuiteError('');
        let payload, assertions;
        try { payload = JSON.parse(editingCaseDraft.trigger_payload || '{}'); } catch { setTestSuiteError('trigger_payload is not valid JSON'); return; }
        try { assertions = JSON.parse(editingCaseDraft.assertions || '[]'); } catch { setTestSuiteError('assertions is not valid JSON'); return; }
        try {
            await axios.put(`${API_BASE_URL}/workflows/${workflowId}/test-suite/${caseId}`, {
                name: editingCaseDraft.name,
                trigger_payload: payload,
                assertions,
            }, { headers: getAuthHeaders() });
            setEditingCaseId(null);
            setEditingCaseDraft(null);
            fetchTestCases();
        } catch (err) {
            setTestSuiteError(err.response?.data?.detail || 'Failed to update test case');
        }
    };

    const handleDeleteTestCase = async (caseId) => {
        if (!window.confirm('Delete this test case?')) return;
        try {
            await axios.delete(`${API_BASE_URL}/workflows/${workflowId}/test-suite/${caseId}`, { headers: getAuthHeaders() });
            fetchTestCases();
            if (testResults) setTestResults(prev => prev ? { ...prev, results: prev.results.filter(r => r.id !== caseId) } : prev);
        } catch (err) {
            setTestSuiteError(err.response?.data?.detail || 'Failed to delete test case');
        }
    };

    const handleDeleteRun = async (e, runId) => {
        e.stopPropagation();
        if (!(await window.confirm("Are you sure you want to delete this execution?"))) return;
        try {
            await axios.delete(`${API_BASE_URL}/runs/${runId}`, { headers: getAuthHeaders() });
            setSelectedRunIds(prev => prev.filter(id => id !== runId));
            fetchRuns(currentPage, appliedFilters);
        } catch (error) {
            console.error("Failed to delete run", error);
            alert("Failed to delete execution");
        }
    };

    const handleBulkDeleteRuns = async () => {
        if (selectedRunIds.length === 0) return;
        if (!(await window.confirm(`Are you sure you want to delete ${selectedRunIds.length} selected executions?`))) return;
        try {
            await axios.post(
                `${API_BASE_URL}/bulk/runs/delete`,
                { run_ids: selectedRunIds },
                { headers: getAuthHeaders() }
            );
            setSelectedRunIds([]);
            setSelectedRun(null);
            fetchRuns(currentPage, appliedFilters);
        } catch (error) {
            console.error("Failed to bulk delete runs", error);
            alert("Failed to delete selected executions");
        }
    };

    const toggleRunSelection = (e, runId) => {
        e.stopPropagation();
        setSelectedRunIds(prev => prev.includes(runId) ? prev.filter(id => id !== runId) : [...prev, runId]);
    };

    const toggleSelectAllRuns = () => {
        if (runs.length === 0) {
            setSelectedRunIds([]);
            return;
        }
        const allSelected = runs.every(run => selectedRunIds.includes(run.id));
        if (allSelected) {
            setSelectedRunIds([]);
        } else {
            setSelectedRunIds(runs.map(run => run.id));
        }
    };

    const handleDeleteAllRuns = async () => {
        if (!(await window.confirm("Are you sure you want to delete ALL execution history for this workflow? This action cannot be undone."))) return;
        try {
            await axios.delete(`${API_BASE_URL}/workflows/${workflowId}/runs`, { headers: getAuthHeaders() });
            setCurrentPage(1);
            fetchRuns(1, appliedFilters);
        } catch (error) {
            console.error("Failed to delete all runs", error);
            alert("Failed to delete all executions");
        }
    };

    const fetchRunDetails = async (runId) => {
        try {
            const res = await axios.get(`${API_BASE_URL}/runs/${runId}`, { headers: getAuthHeaders() });
            setSelectedRun(res.data);
        } catch (error) {
            console.error("Failed to fetch run details", error);
            alert("Failed to fetch run details");
        }
    };

    const handleReplayNode = async (nodeId) => {
        setNodeReplay(prev => ({ ...prev, [nodeId]: { loading: true, result: null } }));
        try {
            const res = await axios.post(
                `${API_BASE_URL}/runs/${selectedRun.id}/replay-node`,
                { node_id: nodeId },
                { headers: getAuthHeaders() }
            );
            setNodeReplay(prev => ({ ...prev, [nodeId]: { loading: false, result: res.data } }));
        } catch (err) {
            setNodeReplay(prev => ({
                ...prev,
                [nodeId]: { loading: false, result: { success: false, error: err.message || 'Request failed' } }
            }));
        }
    };

    const handleResumeFrom = async (nodeId) => {
        setNodeResume(prev => ({ ...prev, [nodeId]: { loading: true, result: null } }));
        try {
            const res = await axios.post(
                `${API_BASE_URL}/runs/${selectedRun.id}/resume-from-node/${nodeId}`,
                {},
                { headers: getAuthHeaders() }
            );
            setNodeResume(prev => ({ ...prev, [nodeId]: { loading: false, result: { success: true, ...res.data } } }));
        } catch (err) {
            const msg = err.response?.data?.detail || err.message || 'Resume failed';
            setNodeResume(prev => ({
                ...prev,
                [nodeId]: { loading: false, result: { success: false, error: msg } }
            }));
        }
    };

    const incomingTargets = new Set((edges || []).map(edge => edge.target));
    const firstNode = (nodes || []).find(node => !incomingTargets.has(node.id)) || (nodes || [])[0];
    const firstNodeOutput = firstNode && selectedRun?.outputs?.nodes
        ? selectedRun.outputs.nodes[firstNode.id]?.data
        : null;
    const executionOrder = selectedRun?.outputs?.env?._execution_order || {};
    const errorMap = selectedRun?.outputs?.env?._errors || {};
    const failedNodeId = selectedRun?.outputs?.env?._last_failed_node;
    const errorEntries = Object.entries(errorMap || {});
    const failedNodeLabel = failedNodeId
        ? (nodes || []).find(node => node.id === failedNodeId)?.data?.label || failedNodeId
        : null;
    const diagramNodes = (nodes || []).map((node, index) => {
        const order = executionOrder[node.id];
        const baseLabel = node.data?.label || node.id;
        const failed = Boolean(errorMap[node.id]) || failedNodeId === node.id;
        const executed = Boolean(selectedRun?.outputs?.nodes?.[node.id]);
        const label = order ? `${order}. ${baseLabel}` : baseLabel;
        const position = node.position || { x: (index % 4) * 200, y: Math.floor(index / 4) * 120 };
        return {
            id: node.id,
            position,
            data: { label: failed ? `${label} (error)` : label },
            style: {
                padding: '8px',
                borderRadius: '6px',
                border: failed ? '2px solid var(--error-color)' : executed ? '2px solid var(--success-color)' : '1px solid var(--border-node)',
                background: 'var(--bg-node)',
                color: 'var(--text-primary)',
                opacity: executed || failed ? 1 : 0.5,
                fontSize: '12px'
            }
        };
    });
    const diagramEdges = (edges || []).map(edge => ({
        id: edge.id || `e${edge.source}-${edge.target}`,
        source: edge.source,
        target: edge.target,
        label: edge.label || edge.data?.condition
    }));

    // Sorted node IDs for display in execution order
    const sortedNodeIds = Object.entries(executionOrder)
        .sort((a, b) => a[1] - b[1])
        .map(([id]) => id);

    return (
        <div style={modalOverlayStyle} onClick={onClose}>
            <div style={modalContentStyle} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ margin: 0 }}>Execution History</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
                            <input
                                type="checkbox"
                                checked={saveHistory}
                                onChange={toggleSaveHistory}
                                style={{ marginRight: '8px' }}
                            />
                            Save History
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
                            <input
                                type="checkbox"
                                checked={autoRefresh}
                                onChange={(e) => setAutoRefresh(e.target.checked)}
                                style={{ marginRight: '8px' }}
                            />
                            Auto Refresh
                        </label>
                        <button
                            onClick={handleDeleteAllRuns}
                            style={{ padding: '5px 10px', background: 'var(--error-color)', color: 'var(--text-inverse)', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                            Delete All
                        </button>
                        <button
                            onClick={handleBulkDeleteRuns}
                            disabled={selectedRunIds.length === 0}
                            style={{ padding: '5px 10px', background: selectedRunIds.length === 0 ? 'var(--button-bg)' : 'var(--error-color)', color: selectedRunIds.length === 0 ? 'var(--text-tertiary)' : 'var(--text-inverse)', border: 'none', borderRadius: '4px', cursor: selectedRunIds.length === 0 ? 'not-allowed' : 'pointer' }}
                        >
                            Delete Selected
                        </button>
                        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '20px' }}>×</button>
                    </div>
                </div>

                {/* Tab bar */}
                <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-color)', marginBottom: '16px' }}>
                    {['history', 'tests'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => { setActiveTab(tab); setSelectedRun(null); }}
                            style={{
                                padding: '8px 20px',
                                background: 'none',
                                border: 'none',
                                borderBottom: activeTab === tab ? '2px solid var(--accent-color)' : '2px solid transparent',
                                marginBottom: '-2px',
                                color: activeTab === tab ? 'var(--accent-color)' : 'var(--text-secondary)',
                                fontWeight: activeTab === tab ? 700 : 400,
                                fontSize: '13px',
                                cursor: 'pointer',
                                textTransform: 'capitalize',
                            }}
                        >
                            {tab === 'history' ? 'Runs' : 'Tests'}
                        </button>
                    ))}
                </div>

                {activeTab === 'tests' ? (
                    <div>
                        {/* Test suite toolbar */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
                            <button
                                onClick={() => { setAddingCase(true); setTestSuiteError(''); }}
                                disabled={addingCase}
                                style={{ padding: '5px 14px', background: 'var(--accent-color)', color: '#fff', border: 'none', borderRadius: '4px', cursor: addingCase ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600 }}
                            >
                                + Add Test Case
                            </button>
                            <button
                                onClick={handleRunTestSuite}
                                disabled={testRunLoading || testCases.length === 0}
                                style={{ padding: '5px 14px', background: testRunLoading || testCases.length === 0 ? 'var(--button-bg)' : 'var(--success-color)', color: testRunLoading || testCases.length === 0 ? 'var(--text-tertiary)' : '#fff', border: 'none', borderRadius: '4px', cursor: testRunLoading || testCases.length === 0 ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600 }}
                            >
                                {testRunLoading ? '⟳ Running…' : '▶ Run All Tests'}
                            </button>
                            {testResults && (
                                <button
                                    onClick={handleDownloadJUnit}
                                    style={{ padding: '5px 14px', background: 'var(--button-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                                >
                                    ⤴ JUnit XML
                                </button>
                            )}
                            {testResults && (
                                <span style={{ fontSize: '13px', color: testResults.summary.failed === 0 ? 'var(--success-color)' : 'var(--error-color)', fontWeight: 600 }}>
                                    {testResults.summary.passed}/{testResults.summary.total} passed
                                </span>
                            )}
                        </div>

                        {testSuiteError && (
                            <div style={{ fontSize: '12px', color: 'var(--error-color)', padding: '7px 10px', background: 'rgba(239,68,68,0.08)', borderRadius: '6px', marginBottom: '10px' }}>
                                {testSuiteError}
                            </div>
                        )}

                        {/* Add new test case form */}
                        {addingCase && (
                            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--accent-color)', borderRadius: '8px', padding: '14px', marginBottom: '14px' }}>
                                <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '10px', color: 'var(--accent-color)' }}>New Test Case</div>
                                <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Name</label>
                                <input
                                    type="text"
                                    value={newCaseDraft.name}
                                    onChange={e => setNewCaseDraft(d => ({ ...d, name: e.target.value }))}
                                    placeholder="e.g. Happy path with valid payload"
                                    style={{ width: '100%', padding: '6px 10px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '8px' }}
                                />
                                <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Trigger Payload (JSON)</label>
                                <textarea
                                    value={newCaseDraft.trigger_payload}
                                    onChange={e => setNewCaseDraft(d => ({ ...d, trigger_payload: e.target.value }))}
                                    rows={3}
                                    style={{ width: '100%', padding: '6px 10px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '12px', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box', marginBottom: '8px' }}
                                />
                                <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                                    Assertions (JSON array of {'{path, operator, value}'})
                                    <span style={{ marginLeft: '6px', color: 'var(--text-tertiary)', fontSize: '11px' }}>operators: eq ne contains not_contains gt lt gte lte exists not_exists</span>
                                </label>
                                <textarea
                                    value={newCaseDraft.assertions}
                                    onChange={e => setNewCaseDraft(d => ({ ...d, assertions: e.target.value }))}
                                    rows={3}
                                    placeholder={'[{"path":"status","operator":"eq","value":"completed"}]'}
                                    style={{ width: '100%', padding: '6px 10px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '12px', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box', marginBottom: '10px' }}
                                />
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button onClick={handleCreateTestCase} disabled={!newCaseDraft.name.trim()} style={{ padding: '5px 16px', background: newCaseDraft.name.trim() ? 'var(--accent-color)' : 'var(--button-bg)', color: newCaseDraft.name.trim() ? '#fff' : 'var(--text-tertiary)', border: 'none', borderRadius: '4px', cursor: newCaseDraft.name.trim() ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: 600 }}>Save</button>
                                    <button onClick={() => { setAddingCase(false); setTestSuiteError(''); }} style={{ padding: '5px 14px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
                                </div>
                            </div>
                        )}

                        {/* Test case list */}
                        {testCasesLoading ? (
                            <p style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>Loading test cases…</p>
                        ) : testCases.length === 0 ? (
                            <p style={{ color: 'var(--text-tertiary)', fontSize: '13px', marginTop: '10px' }}>No test cases yet. Add one to get started.</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {testCases.map(tc => {
                                    const result = testResults?.results?.find(r => r.id === tc.id);
                                    const isEditing = editingCaseId === tc.id;
                                    return (
                                        <div key={tc.id} style={{ background: 'var(--bg-secondary)', border: `1px solid ${result ? (result.passed ? 'var(--success-color)' : 'var(--error-color)') : 'var(--border-color)'}`, borderRadius: '8px', padding: '12px 14px' }}>
                                            {isEditing ? (
                                                <div>
                                                    <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Name</label>
                                                    <input
                                                        type="text"
                                                        value={editingCaseDraft.name}
                                                        onChange={e => setEditingCaseDraft(d => ({ ...d, name: e.target.value }))}
                                                        style={{ width: '100%', padding: '5px 8px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '8px' }}
                                                    />
                                                    <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Trigger Payload (JSON)</label>
                                                    <textarea
                                                        value={editingCaseDraft.trigger_payload}
                                                        onChange={e => setEditingCaseDraft(d => ({ ...d, trigger_payload: e.target.value }))}
                                                        rows={3}
                                                        style={{ width: '100%', padding: '5px 8px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '12px', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box', marginBottom: '8px' }}
                                                    />
                                                    <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Assertions (JSON)</label>
                                                    <textarea
                                                        value={editingCaseDraft.assertions}
                                                        onChange={e => setEditingCaseDraft(d => ({ ...d, assertions: e.target.value }))}
                                                        rows={3}
                                                        style={{ width: '100%', padding: '5px 8px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '12px', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box', marginBottom: '10px' }}
                                                    />
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button onClick={() => handleSaveTestCase(tc.id)} style={{ padding: '4px 14px', background: 'var(--accent-color)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>Save</button>
                                                        <button onClick={() => { setEditingCaseId(null); setEditingCaseDraft(null); }} style={{ padding: '4px 12px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: result ? '8px' : 0 }}>
                                                        <div>
                                                            <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)' }}>{tc.name}</span>
                                                            {result && (
                                                                <span style={{ marginLeft: '10px', fontSize: '12px', fontWeight: 700, color: result.passed ? 'var(--success-color)' : 'var(--error-color)' }}>
                                                                    {result.passed ? '✓ Passed' : '✗ Failed'}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                            <button
                                                                onClick={() => { setEditingCaseId(tc.id); setEditingCaseDraft({ name: tc.name, trigger_payload: JSON.stringify(tc.trigger_payload, null, 2), assertions: JSON.stringify(tc.assertions, null, 2) }); }}
                                                                style={{ padding: '3px 10px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                                                            >Edit</button>
                                                            <button
                                                                onClick={() => handleDeleteTestCase(tc.id)}
                                                                style={{ padding: '3px 10px', background: 'transparent', color: 'var(--error-color)', border: '1px solid var(--error-color)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                                                            >Delete</button>
                                                        </div>
                                                    </div>
                                                    {/* Assertion results after a run */}
                                                    {result && result.assertions.length > 0 && (
                                                        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                            {result.assertions.map((a, i) => (
                                                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                                                                    <span style={{ color: a.passed ? 'var(--success-color)' : 'var(--error-color)', fontWeight: 700 }}>{a.passed ? '✓' : '✗'}</span>
                                                                    <code style={{ background: 'var(--bg-input)', padding: '1px 6px', borderRadius: '3px', fontSize: '11px', color: 'var(--text-secondary)' }}>{a.path} {a.operator} {a.expected ?? ''}</code>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {result && result.error && (
                                                        <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--error-color)' }}>{result.error}</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* GitHub Actions snippet */}
                        {testCases.length > 0 && (
                            <details style={{ marginTop: '20px' }}>
                                <summary style={{ cursor: 'pointer', fontSize: '12px', color: 'var(--text-secondary)', userSelect: 'none', padding: '4px 0' }}>
                                    GitHub Actions integration snippet
                                </summary>
                                <pre style={{ background: 'var(--bg-input)', padding: '12px', borderRadius: '6px', fontSize: '11px', color: 'var(--text-secondary)', overflow: 'auto', whiteSpace: 'pre', marginTop: '8px' }}>{`name: xavvy Test Suite
on: [push, pull_request]
jobs:
  test:
    runs-on: ubuntu-latest
    steps:
      - name: Run workflow test suite
        run: |
          curl -s -X POST \\
            -H "Authorization: Bearer $\${XAVVY_API_KEY}" \\
            $\${XAVVY_URL}/workflows/${workflowId}/test-suite/run \\
            -o results.json
          cat results.json

      - name: Download JUnit report
        run: |
          curl -s -H "Authorization: Bearer $\${XAVVY_API_KEY}" \\
            $\${XAVVY_URL}/workflows/${workflowId}/test-suite/junit.xml \\
            -o junit.xml

      - name: Publish test results
        uses: mikepenz/action-junit-report@v4
        with:
          report_paths: junit.xml`}</pre>
                            </details>
                        )}
                    </div>
                ) : (
                <>

                {!selectedRun ? (
                    <>
                        {/* Filter bar */}
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                <button
                                    onClick={() => setShowFilters(f => !f)}
                                    style={{ padding: '5px 12px', background: showFilters ? 'var(--accent-color)' : 'var(--button-bg)', color: showFilters ? 'var(--text-inverse)' : 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                                >
                                    🔍 {showFilters ? 'Hide Filters' : 'Filter & Search'}
                                    {Object.values(appliedFilters).some(v => v) && <span style={{ marginLeft: '6px', background: 'var(--accent-color)', color: 'white', borderRadius: '999px', padding: '1px 6px', fontSize: '11px' }}>active</span>}
                                </button>
                                <button
                                    onClick={exportCsv}
                                    disabled={exportLoading}
                                    style={{ padding: '5px 12px', background: 'var(--button-bg)', color: exportLoading ? 'var(--text-tertiary)' : 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: exportLoading ? 'not-allowed' : 'pointer', fontSize: '13px' }}
                                >
                                    {exportLoading ? '⟳ Exporting…' : '⤴ Export CSV'}
                                </button>
                            </div>

                            {showFilters && (
                                <div style={{ marginTop: '10px', padding: '14px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '10px' }}>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                            Status
                                            <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} style={{ padding: '5px 8px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '13px' }}>
                                                <option value="">All</option>
                                                <option value="completed">Completed</option>
                                                <option value="failed">Failed</option>
                                                <option value="running">Running</option>
                                                <option value="queued">Queued</option>
                                                <option value="paused">Paused</option>
                                            </select>
                                        </label>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                            Trigger
                                            <select value={filters.trigger_type} onChange={e => setFilters(f => ({ ...f, trigger_type: e.target.value }))} style={{ padding: '5px 8px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '13px' }}>
                                                <option value="">All</option>
                                                <option value="manual">Manual</option>
                                                <option value="cron">Cron</option>
                                                <option value="webhook">Webhook</option>
                                                <option value="polling_trigger">Polling Trigger</option>
                                                <option value="chat">Chat</option>
                                            </select>
                                        </label>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                            From (local date/time)
                                            <input type="datetime-local" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} style={{ padding: '5px 8px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '13px' }} />
                                        </label>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                            To (local date/time)
                                            <input type="datetime-local" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} style={{ padding: '5px 8px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '13px' }} />
                                        </label>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                            Min duration (s)
                                            <input type="number" min="0" step="0.1" placeholder="e.g. 1.5" value={filters.min_duration_s} onChange={e => setFilters(f => ({ ...f, min_duration_s: e.target.value }))} style={{ padding: '5px 8px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '13px' }} />
                                        </label>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                            Max duration (s)
                                            <input type="number" min="0" step="0.1" placeholder="e.g. 60" value={filters.max_duration_s} onChange={e => setFilters(f => ({ ...f, max_duration_s: e.target.value }))} style={{ padding: '5px 8px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '13px' }} />
                                        </label>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)', gridColumn: 'span 2' }}>
                                            Error message contains
                                            <input type="text" placeholder="Search error messages…" value={filters.error_contains} onChange={e => setFilters(f => ({ ...f, error_contains: e.target.value }))} onKeyDown={e => e.key === 'Enter' && applyFilters()} style={{ padding: '5px 8px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '13px' }} />
                                        </label>
                                    </div>

                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                        <button onClick={applyFilters} style={{ padding: '5px 14px', background: 'var(--accent-color)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>Apply</button>
                                        <button onClick={clearFilters} style={{ padding: '5px 14px', background: 'var(--button-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Clear</button>

                                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                placeholder="Preset name…"
                                                value={presetName}
                                                onChange={e => setPresetName(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && savePreset()}
                                                style={{ padding: '4px 8px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '12px', width: '120px' }}
                                            />
                                            <button onClick={savePreset} disabled={!presetName.trim()} style={{ padding: '4px 10px', background: presetName.trim() ? 'var(--success-color)' : 'var(--button-bg)', color: presetName.trim() ? 'white' : 'var(--text-tertiary)', border: 'none', borderRadius: '4px', cursor: presetName.trim() ? 'pointer' : 'not-allowed', fontSize: '12px' }}>Save preset</button>
                                        </div>
                                    </div>

                                    {presets.length > 0 && (
                                        <div style={{ marginTop: '10px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                                            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Saved:</span>
                                            {presets.map((p, i) => (
                                                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '12px' }}>
                                                    <button onClick={() => loadPreset(p)} style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', padding: 0, fontSize: '12px' }}>{p.name}</button>
                                                    <button onClick={() => deletePreset(i)} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 0, fontSize: '11px', lineHeight: 1 }}>×</button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {loading ? (
                            <p>Loading history...</p>
                        ) : runs.length === 0 ? (
                            <p style={{ color: 'var(--text-tertiary)', marginTop: '20px' }}>No execution history found{Object.values(appliedFilters).some(v => v) ? ' matching your filters' : ' for this workflow'}.</p>
                        ) : (
                            <>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...thStyle, width: '42px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={runs.length > 0 && runs.every(run => selectedRunIds.includes(run.id))}
                                                    onChange={toggleSelectAllRuns}
                                                />
                                            </th>
                                            <th style={thStyle}>Status</th>
                                            <th style={thStyle}>Trigger</th>
                                            <th style={thStyle}>Start Time</th>
                                            <th style={thStyle}>Duration</th>
                                            <th style={thStyle}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {runs.map(run => {
                                            const duration = run.duration_s != null ? run.duration_s + 's' : (() => {
                                                const startMs = toEpochMs(run.start_time);
                                                const endMs = toEpochMs(run.end_time);
                                                if (startMs == null || endMs == null) return '-';
                                                return ((endMs - startMs) / 1000).toFixed(2) + 's';
                                            })();
                                            const triggerLabel = run.trigger_type || '—';
                                            return (
                                                <tr key={run.id} style={{ cursor: 'pointer', borderBottom: '1px solid var(--border-color)' }} onClick={() => fetchRunDetails(run.id)} className="history-row">
                                                    <td style={tdStyle}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedRunIds.includes(run.id)}
                                                            onChange={(e) => toggleRunSelection(e, run.id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </td>
                                                    <td style={tdStyle}>
                                                        <span style={statusStyle(run.status)}>{run.status}</span>
                                                        {run.priority === 'high' && (
                                                            <span title="High priority — jumps the queue" style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '999px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#fca5a5' }}>HIGH</span>
                                                        )}
                                                        {run.priority === 'low' && (
                                                            <span title="Low priority — yields to others" style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '999px', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.4)', color: '#93c5fd' }}>LOW</span>
                                                        )}
                                                    </td>
                                                    <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: '12px' }}>{triggerLabel}</td>
                                                    <td style={tdStyle}>{formatLocalDateTime(run.start_time)}</td>
                                                    <td style={tdStyle}>{duration}</td>
                                                    <td style={tdStyle}>
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); fetchRunDetails(run.id); }}
                                                                style={{ padding: '5px 10px', background: 'var(--accent-color)', color: 'var(--text-inverse)', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                                            >
                                                                View
                                                            </button>
                                                            <button
                                                                onClick={(e) => handleDeleteRun(e, run.id)}
                                                                style={{ padding: '5px 10px', background: 'var(--error-color)', color: 'var(--text-inverse)', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {total > limit && (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px', gap: '15px' }}>
                                        <button
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            style={{ padding: '5px 10px', background: currentPage === 1 ? 'var(--button-bg)' : 'var(--accent-color)', color: currentPage === 1 ? 'var(--text-tertiary)' : 'var(--text-inverse)', border: 'none', borderRadius: '4px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                                        >
                                            Previous
                                        </button>
                                        <span>Page {currentPage} of {Math.ceil(total / limit)}</span>
                                        <button
                                            disabled={currentPage * limit >= total}
                                            onClick={() => setCurrentPage(prev => prev + 1)}
                                            style={{ padding: '5px 10px', background: currentPage * limit >= total ? 'var(--button-bg)' : 'var(--accent-color)', color: currentPage * limit >= total ? 'var(--text-tertiary)' : 'var(--text-inverse)', border: 'none', borderRadius: '4px', cursor: currentPage * limit >= total ? 'not-allowed' : 'pointer' }}
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    <div>
                        {/* Run detail header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px 0' }}>
                            <button
                                onClick={() => setSelectedRun(null)}
                                style={{ padding: '5px 10px', background: 'transparent', color: 'var(--accent-color)', border: '1px solid var(--accent-color)', borderRadius: '4px', cursor: 'pointer' }}
                            >
                                ← Back to List
                            </button>

                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                {/* Step-Through Debugger toggle */}
                                <button
                                    onClick={() => setStepThroughMode(m => !m)}
                                    style={{
                                        padding: '6px 12px',
                                        background: stepThroughMode ? 'var(--accent-color)' : 'var(--button-bg)',
                                        color: stepThroughMode ? 'var(--text-inverse)' : 'var(--text-primary)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: stepThroughMode ? 'bold' : 'normal'
                                    }}
                                    title="Walk through each node's output step by step and replay individual nodes"
                                >
                                    🐞 {stepThroughMode ? 'Exit Debugger' : 'Step-Through Debugger'}
                                </button>

                                {onPinTriggerData && (
                                    <>
                                        {!firstNodeOutput && (
                                            <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                                                No first node output found
                                            </span>
                                        )}
                                        <button
                                            onClick={() => onPinTriggerData(firstNodeOutput, selectedRun, firstNode)}
                                            disabled={!firstNodeOutput || !firstNode}
                                            style={{ padding: '6px 12px', background: firstNodeOutput ? 'var(--success-color)' : 'var(--button-bg)', color: firstNodeOutput ? 'var(--text-inverse)' : 'var(--text-tertiary)', border: 'none', borderRadius: '4px', cursor: firstNodeOutput ? 'pointer' : 'not-allowed' }}
                                        >
                                            Pin First Node Output
                                        </button>
                                    </>
                                )}
                                {onLoadRunOutputs && (
                                    <button
                                        disabled={loadingRunOutputs}
                                        onClick={async () => {
                                            setLoadingRunOutputs(true);
                                            try {
                                                const token = localStorage.getItem('token');
                                                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                                                const res = await fetch(`${API_BASE_URL}/workflow-runs/${selectedRun.id}/node-outputs`, { headers });
                                                if (!res.ok) throw new Error('Failed to load run outputs');
                                                const data = await res.json();
                                                onLoadRunOutputs(data);
                                            } catch {
                                                alert('Failed to load run outputs onto canvas.');
                                            } finally {
                                                setLoadingRunOutputs(false);
                                            }
                                        }}
                                        style={{ padding: '6px 12px', background: 'var(--accent-color)', color: '#fff', border: 'none', borderRadius: '4px', cursor: loadingRunOutputs ? 'not-allowed' : 'pointer', opacity: loadingRunOutputs ? 0.7 : 1 }}
                                        title="Load this run's node outputs onto the canvas so you can inspect each node's data"
                                    >
                                        {loadingRunOutputs ? 'Loading…' : '🔍 Inspect in Canvas'}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Step-Through Debugger panel */}
                        {stepThroughMode ? (
                            <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '8px', border: '1px solid var(--accent-color)', marginTop: '10px' }}>
                                <h3 style={{ margin: '0 0 14px 0', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    🐞 Step-Through Debugger
                                    <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: 'normal' }}>
                                        — walk through each executed node and replay with original inputs
                                    </span>
                                </h3>
                                <StepThroughDebugger
                                    run={selectedRun}
                                    nodes={nodes}
                                    onClose={() => setStepThroughMode(false)}
                                    getAuthHeaders={getAuthHeaders}
                                />
                            </div>
                        ) : (
                            <div style={{ background: 'var(--bg-secondary)', padding: '15px', borderRadius: '4px', marginTop: '10px' }}>
                                {/* Metadata */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                                    <div><strong>Status:</strong> <span style={statusStyle(selectedRun.status)}>{selectedRun.status}</span></div>
                                    <div><strong>Run ID:</strong> <span style={{ fontFamily: 'monospace', color: 'var(--text-tertiary)' }}>{selectedRun.id}</span></div>
                                    <div><strong>Start:</strong> {formatLocalDateTime(selectedRun.start_time)}</div>
                                    <div><strong>End:</strong> {selectedRun.end_time ? formatLocalDateTime(selectedRun.end_time) : '-'}</div>
                                </div>

                                {selectedRun.status === 'failed' && (
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                                        <button
                                            onClick={() => setCopilotState({ isOpen: true, mode: 'fix', runId: selectedRun.id })}
                                            style={{
                                                padding: '8px 16px',
                                                backgroundColor: '#8b5cf6',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontWeight: '500',
                                                fontSize: '14px',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                            }}
                                        >
                                            <span>✨</span> Fix with AI
                                        </button>
                                    </div>
                                )}

                                {selectedRun.error && (
                                    <div style={{ color: 'var(--error-color)', padding: '10px', border: '1px solid var(--error-color)', borderRadius: '4px', marginBottom: '15px' }}>
                                        <strong>Error:</strong> {selectedRun.error}
                                    </div>
                                )}
                                {(failedNodeId || errorEntries.length > 0) && (
                                    <div style={{ background: 'var(--bg-input)', padding: '10px', borderRadius: '4px', marginBottom: '15px', border: '1px solid var(--border-color)' }}>
                                        {failedNodeId && (
                                            <div style={{ marginBottom: errorEntries.length > 0 ? '8px' : 0 }}>
                                                <strong>Failed Node:</strong> {failedNodeLabel} <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>({failedNodeId})</span>
                                            </div>
                                        )}
                                        {errorEntries.length > 0 && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                {errorEntries.map(([nodeId, message]) => {
                                                    const nodeLabel = (nodes || []).find(node => node.id === nodeId)?.data?.label || nodeId;
                                                    return (
                                                        <div key={nodeId} style={{ color: 'var(--error-color)', fontSize: '12px' }}>
                                                            {nodeLabel} <span style={{ color: 'var(--text-tertiary)' }}>({nodeId})</span>: {message}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <h3 style={{ marginTop: '20px', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>Execution Diagram</h3>
                                <div style={{ height: '320px', background: 'var(--bg-node)', border: '1px solid var(--border-color)', borderRadius: '6px', marginBottom: '15px' }}>
                                    <ReactFlowProvider>
                                        <ReactFlow
                                            nodes={diagramNodes}
                                            edges={diagramEdges}
                                            fitView
                                            nodesDraggable={false}
                                            nodesConnectable={false}
                                            elementsSelectable={false}
                                            zoomOnScroll={false}
                                            panOnScroll
                                            style={{ background: 'var(--bg-node)' }}
                                        >
                                            <Background variant="dots" gap={12} size={1} color="var(--text-tertiary)" />
                                        </ReactFlow>
                                    </ReactFlowProvider>
                                </div>

                                <h3 style={{ marginTop: '20px', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>Node Outputs</h3>
                                {selectedRun.outputs && selectedRun.outputs.nodes ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {/* Show nodes in execution order when available, then any remaining */}
                                        {[
                                            ...sortedNodeIds.filter(id => selectedRun.outputs.nodes[id]),
                                            ...Object.keys(selectedRun.outputs.nodes).filter(id => !sortedNodeIds.includes(id))
                                        ].map(key => {
                                            const value = selectedRun.outputs.nodes[key];
                                            const node = nodes?.find(n => n.id === key);
                                            const nodeLabel = node ? node.data.label : key;
                                            const order = executionOrder[key];
                                            const hasNodeError = Boolean(errorMap[key]);
                                            const replay = nodeReplay[key];

                                            const resume = nodeResume[key];
                                            const hasCheckpoint = Boolean(selectedRun?.checkpoint_data);
                                            return (
                                                <div key={key} style={{ background: 'var(--bg-input)', padding: '10px', borderRadius: '4px', border: `1px solid ${hasNodeError ? 'var(--error-color)' : 'var(--border-color)'}` }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                                        <div style={{ fontWeight: 'bold', color: hasNodeError ? 'var(--error-color)' : 'var(--success-color)' }}>
                                                            {order ? `${order}. ` : ''}{nodeLabel}
                                                            <span style={{ fontSize: '0.8em', color: 'var(--text-tertiary)', fontWeight: 'normal', marginLeft: '6px' }}>({key})</span>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                        {hasCheckpoint && (
                                                            <button
                                                                onClick={() => handleResumeFrom(key)}
                                                                disabled={resume?.loading}
                                                                title="Resume the workflow from this node forward using the saved checkpoint"
                                                                style={{
                                                                    padding: '3px 10px',
                                                                    background: resume?.loading ? 'var(--button-bg)' : 'transparent',
                                                                    color: resume?.loading ? 'var(--text-tertiary)' : 'var(--success-color)',
                                                                    border: '1px solid var(--success-color)',
                                                                    borderRadius: '4px',
                                                                    cursor: resume?.loading ? 'not-allowed' : 'pointer',
                                                                    fontSize: '12px'
                                                                }}
                                                            >
                                                                {resume?.loading ? '⟳ Resuming…' : '▶ Resume from here'}
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handleReplayNode(key)}
                                                            disabled={replay?.loading}
                                                            title="Re-execute this node with its original inputs"
                                                            style={{
                                                                padding: '3px 10px',
                                                                background: replay?.loading ? 'var(--button-bg)' : 'transparent',
                                                                color: replay?.loading ? 'var(--text-tertiary)' : 'var(--accent-color)',
                                                                border: '1px solid var(--accent-color)',
                                                                borderRadius: '4px',
                                                                cursor: replay?.loading ? 'not-allowed' : 'pointer',
                                                                fontSize: '12px'
                                                            }}
                                                        >
                                                            {replay?.loading ? '⟳ Replaying…' : '↩ Replay'}
                                                        </button>
                                                        </div>
                                                    </div>

                                                    <JsonEditor
                                                        value={JSON.stringify(value.data, null, 2)}
                                                        onChange={() => {}}
                                                        readOnly={true}
                                                        style={{ minHeight: '50px', border: 'none', background: 'transparent', padding: 0 }}
                                                    />

                                                    {/* Inline replay result */}
                                                    {replay?.result && (
                                                        <div style={{ marginTop: '10px', borderTop: '1px dashed var(--border-color)', paddingTop: '8px' }}>
                                                            <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', color: replay.result.success ? 'var(--success-color)' : 'var(--error-color)' }}>
                                                                {replay.result.success ? '✓ Replay result' : '✗ Replay failed'}
                                                                {replay.result.duration_ms !== undefined && ` (${replay.result.duration_ms}ms)`}
                                                            </div>
                                                            {replay.result.success ? (
                                                                <JsonEditor
                                                                    value={JSON.stringify(replay.result.output, null, 2)}
                                                                    onChange={() => {}}
                                                                    readOnly={true}
                                                                    style={{ minHeight: '40px', border: 'none', background: 'transparent', padding: 0 }}
                                                                />
                                                            ) : (
                                                                <div style={{ color: 'var(--error-color)', fontSize: '12px' }}>{replay.result.error}</div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Inline resume result */}
                                                    {resume?.result && (
                                                        <div style={{ marginTop: '10px', borderTop: '1px dashed var(--border-color)', paddingTop: '8px' }}>
                                                            <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', color: resume.result.success ? 'var(--success-color)' : 'var(--error-color)' }}>
                                                                {resume.result.success
                                                                    ? `✓ Resumed — new run ${resume.result.status} (ID: ${resume.result.run_id})`
                                                                    : `✗ Resume failed: ${resume.result.error}`}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p style={{ color: 'var(--text-tertiary)' }}>No output data available.</p>
                                )}
                            </div>
                        )}
                    </div>
                )}
                </>
                )}
            </div>

            <CopilotModal
                isOpen={copilotState.isOpen}
                mode={copilotState.mode}
                workflowId={workflowId}
                runId={copilotState.runId}
                onClose={() => setCopilotState(prev => ({ ...prev, isOpen: false }))}
            />
        </div>
    );
}
